import React from 'react';
import { formatDate } from '../../utils/dateFormatter';
import { TEXT, reviewsCountText } from '../../config/listingPerformanceLabels';
import { formatFigure } from './parts';

/**
 * «أداء الوحدات» — the unit's two 8-week charts, hand-drawn inline SVG (no chart library — plan §9).
 * Same rules as the sheet's: oldest week on the RIGHT, newest on the left (RTL); occupancy bars in the
 * week's band colour with the target as a dashed line; review bars split 5★ / 4★ / ≤3★ with the week's
 * average stars on top. A week with no figure has no bar and says «—» — never a zero-height bar.
 * Each bar carries an SVG <title>: the hover text and what a screen reader announces.
 * Pattern reference: components/revenue/MonthlyReports/RevenueTrendChart.js (fixed viewBox, CSS-scaled).
 */

// Taller than wide-and-flat: the two charts sit side by side above the table (§3c, 15 Oct review), so
// each is a half-width card where 8 bars must read at a glance.
const VIEW_W = 560;
const VIEW_H = 260;
const PAD = { top: 26, bottom: 46, side: 8 };
const PLOT_H = VIEW_H - PAD.top - PAD.bottom;
const BAR_W = 38;

const BAND = { green: '#48b58d', amber: '#ff9f53', red: '#DC3545' };
const NEUTRAL = '#8A6BC4';
const STARS = { five: '#48b58d', four: '#ff9f53', threeOrLess: '#DC3545', unknown: NEUTRAL };

const weekLabel = (date) => formatDate(date, { year: undefined, month: 'short' });
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

/** x of slot i, counting from the RIGHT edge (i = 0 is the oldest week). */
const slotX = (i, n) => {
  const slot = (VIEW_W - PAD.side * 2) / Math.max(n, 1);
  return VIEW_W - PAD.side - slot * (i + 0.5);
};

const Axis = ({ points }) => points.map((p, i) => (
  <text key={p.weekEnd} x={slotX(i, points.length)} y={VIEW_H - 8} textAnchor="middle" className="lp-chart__axis">
    {weekLabel(p.weekEnd)}
  </text>
));

/** A mark under each week in which something was changed on the unit — read the bar next to the log. */
const ChangeMarks = ({ points }) => points.map((p, i) => (p.changes > 0 ? (
  <g key={`c-${p.weekEnd}`} className="lp-chart__change">
    <title>{`${weekLabel(p.weekEnd)}: ${p.changes} ${TEXT.changesInWeek}`}</title>
    <circle cx={slotX(i, points.length)} cy={VIEW_H - PAD.bottom + 14} r="5" />
    {p.changes > 1 && <text x={slotX(i, points.length) + 9} y={VIEW_H - PAD.bottom + 18} className="lp-chart__change-count" direction="ltr">{p.changes}</text>}
  </g>
) : null));

export const OccupancyChart = ({ points = [], target }) => {
  if (!points.length) return null;
  const y = (pct) => PAD.top + PLOT_H * (1 - Math.min(100, Math.max(0, pct)) / 100);
  return (
    <figure className="lp-chart">
      <figcaption className="lp-chart__title">{TEXT.occupancyChart}</figcaption>
      <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} role="img" aria-label={TEXT.occupancyChart} className="lp-chart__svg">
        <line x1={PAD.side} x2={VIEW_W - PAD.side} y1={y(0)} y2={y(0)} className="lp-chart__base" />
        {isNum(target) && (
          <>
            <line x1={PAD.side} x2={VIEW_W - PAD.side} y1={y(target)} y2={y(target)} className="lp-chart__target" />
            {/* The line names itself — no legend line under the chart. */}
            <text x={VIEW_W - PAD.side} y={y(target) - 5} textAnchor="start" direction="rtl" className="lp-chart__target-label">
              {TEXT.targetLabel}{' '}
              {/* «60%», as everywhere on the page — never «%60» (bidi). */}
              <tspan direction="ltr" unicodeBidi="embed">{`${formatFigure(target, 'count')}%`}</tspan>
            </text>
          </>
        )}
        {points.map((p, i) => {
          const x = slotX(i, points.length);
          if (!isNum(p.value)) {
            return <text key={p.weekEnd} x={x} y={y(0) - 6} textAnchor="middle" className="lp-chart__none">—</text>;
          }
          const top = y(p.value);
          return (
            <g key={p.weekEnd}>
              <rect x={x - BAR_W / 2} y={top} width={BAR_W} height={Math.max(y(0) - top, 1)} rx="2" fill={BAND[p.band] || NEUTRAL}>
                <title>{`${weekLabel(p.weekEnd)}: ${formatFigure(p.value, 'pct1')}`}</title>
              </rect>
              <text x={x} y={top - 6} textAnchor="middle" className="lp-chart__value" direction="ltr">{formatFigure(p.value, 'pct1')}</text>
            </g>
          );
        })}
        <ChangeMarks points={points} />
        <Axis points={points} />
      </svg>
      {/* Only the dot needs a key; the dates show the order and the target line is labelled. */}
      {points.some(p => p.changes > 0) && (
        <p className="lp-chart__note"><span className="lp-key lp-key--change" /> {TEXT.changeKey}</p>
      )}
    </figure>
  );
};

/**
 * Are we getting better or worse ratings? (founder, 5 Oct). Each week's bar is as HIGH as the average stars
 * of the Airbnb reviews that ARRIVED that week (0–5), coloured 5★ green / 4★ amber / ≤3★ red, with the
 * number of reviews under it — so a 3★ week sits visibly lower than a 5★ one. Never the lifetime rating
 * (it moves too slowly to show a bad week; it is in the table). A week with no new review shows «—».
 */
export const ReviewStarsChart = ({ points = [] }) => {
  if (!points.length) return null;
  const base = PAD.top + PLOT_H;
  const y = (stars) => PAD.top + PLOT_H * (1 - Math.min(5, Math.max(0, stars)) / 5);
  return (
    <figure className="lp-chart">
      <figcaption className="lp-chart__title">{TEXT.reviewsChart}</figcaption>
      <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} role="img" aria-label={TEXT.reviewsChart} className="lp-chart__svg">
        {[5, 4, 3].map(s => (
          <g key={s}>
            <line x1={PAD.side} x2={VIEW_W - PAD.side} y1={y(s)} y2={y(s)} className="lp-chart__grid" />
          </g>
        ))}
        <line x1={PAD.side} x2={VIEW_W - PAD.side} y1={base} y2={base} className="lp-chart__base" />
        {points.map((p, i) => {
          const x = slotX(i, points.length);
          if (!isNum(p.count) || p.count === 0) {
            return (
              <text key={p.weekEnd} x={x} y={base - 6} textAnchor="middle" className="lp-chart__none">
                <title>{`${weekLabel(p.weekEnd)}: ${TEXT.noReview}`}</title>—
              </text>
            );
          }
          // Imported sheet weeks may hold a count without an average — a neutral stub, never a guessed height.
          if (!isNum(p.avgStars)) {
            return (
              <g key={p.weekEnd}>
                <title>{`${weekLabel(p.weekEnd)}: ${reviewsCountText(p.count)}`}</title>
                <rect x={x - BAR_W / 2} y={base - 4} width={BAR_W} height={4} fill={STARS.unknown} />
                <text x={x} y={base + 16} textAnchor="middle" className="lp-chart__count">{reviewsCountText(p.count)}</text>
              </g>
            );
          }
          const band = p.avgStars >= 5 ? 'five' : p.avgStars >= 4 ? 'four' : 'threeOrLess';
          const top = y(p.avgStars);
          return (
            <g key={p.weekEnd}>
              <title>{`${weekLabel(p.weekEnd)}: ${TEXT.avgStars} ${formatFigure(p.avgStars, 'avg')}★ — ${reviewsCountText(p.count)}`}</title>
              <rect x={x - BAR_W / 2} y={top} width={BAR_W} height={Math.max(base - top, 1)} rx="2" fill={STARS[band]} />
              <text x={x} y={top - 6} textAnchor="middle" className="lp-chart__value" direction="ltr">{formatFigure(p.avgStars, 'avg')}★</text>
              <text x={x} y={base + 16} textAnchor="middle" className="lp-chart__count">{reviewsCountText(p.count)}</text>
            </g>
          );
        })}
        <Axis points={points} />
      </svg>
      <p className="lp-chart__note">
        <span className="lp-key lp-key--five" /> {TEXT.stars5}
        <span className="lp-key lp-key--four" /> {TEXT.stars4}
        <span className="lp-key lp-key--three" /> {TEXT.stars3}
        <span className="lp-chart__aside"> · {TEXT.reviewsChartNote}</span>
      </p>
    </figure>
  );
};
